// frontend/src/components/Dashboard/ShipmentForm.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useCallback } from "react";
import { createShipment, getAllOrders, getProducts } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShip,
  faBox,
  faTruck,
  faPlane,
  faTimes,
  faPlus,
  faTrash,
  faSave,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faMapMarkerAlt,
  faCalendar,
  faWeightScale,
  faBoxes,
  faUser,
  faEnvelope,
  faPhone,
  faGlobe,
  faAnchor,
  faClipboard,
  faWarehouse,
  faCity,
  faRoad,
  faTrain,
  faShip as faShipIcon,
  faBuilding,
  faLocationDot,
  faCheckCircle,
  faMobileAlt,
  faMoneyBillWave,
  faCreditCard,
  faStore,
  faCrown,
  faShieldAlt,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// CONSTANTS - Globally Sourced (Confidential)
// ============================================
const SUPPLIER = {
  name: "Globally Sourced",
  country: "Globally Sourced",
  city: "Global Supply Chain",
  portLoading: "Global Supply Chain",
  portDischarge: "Freetown, Sierra Leone",
  warehouse: "Global Distribution Center",
};

// ============================================
// MAIN COMPONENT
// ============================================
const ShipmentForm = ({ onClose, onSave, initialOrderId = null }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [formData, setFormData] = useState({
    orderId: initialOrderId || "",
    orderType: "retail",
    origin: {
      city: SUPPLIER.city,
      country: SUPPLIER.country,
      warehouse: SUPPLIER.warehouse,
    },
    destination: {
      city: "Freetown",
      country: "Sierra Leone",
      district: "",
    },
    estimatedDelivery: "",
    shippingMethod: "sea",
    bulkDetails: {
      isBulkShipment: false,
      containerNumber: "",
      containerType: "LCL",
      vesselName: "",
      voyageNumber: "",
      portOfLoading: SUPPLIER.portLoading,
      portOfDischarge: SUPPLIER.portDischarge,
      blNumber: "",
      sealNumber: "",
      weightInKg: 0,
      palletsCount: 0,
    },
    smallShipmentDetails: {
      courierName: "",
      courierTrackingNumber: "",
      packageWeight: 0,
      packageDimensions: { length: 0, width: 0, height: 0 },
    },
    products: [],
    customs: {
      cleared: false,
      clearanceDate: "",
      customsBroker: "",
      dutiesPaid: false,
      dutyAmount: 0,
    },
    notes: "",
    internalNotes: "",
    trackingUrl: "",
    supplier: {
      name: SUPPLIER.name,
      location: SUPPLIER.city,
      country: SUPPLIER.country,
    },
    shippingAddress: {
      recipientName: "",
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
      phone: "",
    },
    customer: {
      name: "",
      email: "",
      phone: "",
      whatsapp: "",
      userType: "customer",
      farmName: "",
    },
  });

  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [showProductSelector, setShowProductSelector] = useState(false);

  // Fetch orders and products on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, productsRes] = await Promise.all([
          getAllOrders({ limit: 100 }),
          getProducts({ limit: 100, isAvailable: true }),
        ]);

        const ordersData = ordersRes?.data?.orders || ordersRes?.data || [];
        const productsData =
          productsRes?.data?.products || productsRes?.data || [];

        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setProducts(Array.isArray(productsData) ? productsData : []);

        if (initialOrderId) {
          const order = ordersData.find((o) => o._id === initialOrderId);
          if (order) handleOrderSelect(initialOrderId);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load orders and products");
      }
    };
    fetchData();
  }, [initialOrderId]);

  const handleOrderSelect = (orderId) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order) return;

    setSelectedOrder(order);
    const isBulk =
      order.orderType === "bulk_farmer" || (order.totalQuantity || 0) >= 100;

    setFormData((prev) => ({
      ...prev,
      orderId: orderId,
      orderType: isBulk ? "bulk_farmer" : "retail",
      products:
        order.items?.map((item) => ({
          productId: item.product?._id || item.product,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit || "kg",
          price: item.price?.usd || 0,
          origin: "Globally Sourced",
        })) || [],
      totalQuantity: order.totalQuantity || 0,
      totalWeight: order.totalQuantity || 0,
      bulkDetails: {
        ...prev.bulkDetails,
        weightInKg: order.totalQuantity || 0,
        palletsCount: Math.ceil((order.totalQuantity || 0) / 500),
        isBulkShipment: isBulk,
        portOfLoading: SUPPLIER.portLoading,
        portOfDischarge: SUPPLIER.portDischarge,
      },
      shippingAddress: {
        recipientName:
          order.shippingAddress?.recipientName || order.userName || "",
        street: order.shippingAddress?.street || "",
        city: order.shippingAddress?.city || "",
        district: order.shippingAddress?.district || "",
        country: order.shippingAddress?.country || "Sierra Leone",
        phone: order.shippingAddress?.phone || order.userPhone || "",
      },
      customer: {
        name: order.userName || "",
        email: order.userEmail || "",
        phone: order.userPhone || "",
        whatsapp: order.userWhatsapp || order.userPhone || "",
        userType: order.userType || "customer",
        farmName: order.farmerDetails?.farmName || "",
      },
      supplier: {
        name: SUPPLIER.name,
        location: SUPPLIER.city,
        country: SUPPLIER.country,
      },
      origin: {
        city: SUPPLIER.city,
        country: SUPPLIER.country,
        warehouse: SUPPLIER.warehouse,
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const parts = name.split(".");
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === "checkbox" ? checked : value,
          },
        }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent]?.[child],
              [grandchild]: type === "checkbox" ? checked : value,
            },
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    setError("");
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      setError("Please select a product");
      return;
    }
    if (productQuantity <= 0) {
      setError("Quantity must be greater than 0");
      return;
    }

    const existingIndex = formData.products.findIndex(
      (p) => p.productId === selectedProduct._id,
    );

    if (existingIndex >= 0) {
      setFormData((prev) => ({
        ...prev,
        products: prev.products.map((p, idx) =>
          idx === existingIndex
            ? { ...p, quantity: p.quantity + productQuantity }
            : p,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        products: [
          ...prev.products,
          {
            productId: selectedProduct._id,
            name: selectedProduct.name,
            quantity: productQuantity,
            unit: selectedProduct.unit || "kg",
            price:
              selectedProduct.basePrice?.usd ||
              selectedProduct.pricing?.usd ||
              0,
            origin: "Globally Sourced",
          },
        ],
      }));
    }

    setSelectedProduct(null);
    setProductQuantity(1);
    setProductSearch("");
    setShowProductSelector(false);
  };

  const handleRemoveProduct = (index) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index),
    }));
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    const found = products.find(
      (p) =>
        p.name.toLowerCase().includes(value.toLowerCase()) ||
        p.slug?.toLowerCase().includes(value.toLowerCase()),
    );
    setSelectedProduct(found || null);
  };

  const calculateTotalWeight = useCallback(() => {
    return formData.products.reduce((sum, p) => sum + (p.quantity || 0), 0);
  }, [formData.products]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!formData.orderId && formData.products.length === 0) {
      setError("Either select an order or add products");
      setLoading(false);
      return;
    }

    try {
      const totalWeight = calculateTotalWeight();

      const shipmentData = {
        orderId: formData.orderId || undefined,
        orderType: formData.orderType,
        origin: {
          city: SUPPLIER.city,
          country: SUPPLIER.country,
          warehouse: SUPPLIER.warehouse,
        },
        destination: formData.destination,
        estimatedDelivery: formData.estimatedDelivery || null,
        shippingMethod: formData.shippingMethod,
        products: formData.products.map((p) => ({
          productId: p.productId,
          name: p.name,
          quantity: p.quantity,
          unit: p.unit || "kg",
          price: p.price || 0,
          origin: "Globally Sourced",
        })),
        totalQuantity: totalWeight,
        totalWeight: totalWeight,
        shippingAddress: formData.shippingAddress,
        customer: formData.customer,
        supplier: {
          name: SUPPLIER.name,
          location: SUPPLIER.city,
          country: SUPPLIER.country,
        },
        notes: formData.notes,
        internalNotes: formData.internalNotes,
        trackingUrl: formData.trackingUrl,
        customs: formData.customs,
      };

      if (
        formData.orderType === "bulk_farmer" ||
        formData.bulkDetails.isBulkShipment
      ) {
        shipmentData.bulkDetails = {
          isBulkShipment: true,
          containerNumber: formData.bulkDetails.containerNumber || "",
          containerType: formData.bulkDetails.containerType || "LCL",
          vesselName: formData.bulkDetails.vesselName || "",
          voyageNumber: formData.bulkDetails.voyageNumber || "",
          portOfLoading: SUPPLIER.portLoading,
          portOfDischarge: SUPPLIER.portDischarge,
          blNumber: formData.bulkDetails.blNumber || "",
          sealNumber: formData.bulkDetails.sealNumber || "",
          weightInKg: formData.bulkDetails.weightInKg || totalWeight,
          palletsCount:
            formData.bulkDetails.palletsCount || Math.ceil(totalWeight / 500),
        };
      }

      if (formData.shippingMethod === "courier") {
        shipmentData.smallShipmentDetails = {
          courierName: formData.smallShipmentDetails.courierName || "",
          courierTrackingNumber:
            formData.smallShipmentDetails.courierTrackingNumber || "",
          packageWeight:
            formData.smallShipmentDetails.packageWeight || totalWeight,
          packageDimensions: formData.smallShipmentDetails
            .packageDimensions || {
            length: 0,
            width: 0,
            height: 0,
          },
        };
      }

      const response = await createShipment(shipmentData);

      if (response.data.success) {
        setSuccess("Shipment created successfully!");
        setTimeout(() => {
          if (onSave) onSave(response.data.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating shipment:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Error creating shipment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isBulkOrder =
    formData.orderType === "bulk_farmer" || formData.bulkDetails.isBulkShipment;
  const isCourier = formData.shippingMethod === "courier";

  const availableOrders = orders.filter(
    (o) => o.status !== "delivered" && o.status !== "cancelled",
  );

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.slug?.toLowerCase().includes(productSearch.toLowerCase()),
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content shipment-form-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faShip} /> Create Shipment
            <span className="supplier-tag">
              <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {error && (
          <div className="alert-message error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        {success && (
          <div className="alert-message success">
            <FontAwesomeIcon icon={faCheck} /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="shipment-form">
          {/* Order Selection */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faBox} /> Order Information
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Select Order (Optional)</label>
                <select
                  value={formData.orderId}
                  onChange={(e) => handleOrderSelect(e.target.value)}
                >
                  <option value="">-- Create Inventory Shipment --</option>
                  {availableOrders.map((order) => (
                    <option key={order._id} value={order._id}>
                      {order.orderNumber} -{" "}
                      {order.userName || order.customerName || "Unknown"}
                      {order.orderType === "bulk_farmer" ? " BULK" : ""}
                      {order.totalQuantity ? ` (${order.totalQuantity}kg)` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Order Type</label>
                <select
                  name="orderType"
                  value={formData.orderType}
                  onChange={handleChange}
                >
                  <option value="retail">Retail</option>
                  <option value="bulk_farmer">Bulk Farmer</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="inventory">Inventory</option>
                </select>
              </div>
            </div>
          </div>

          {/* Supplier & Origin Info - Globally Sourced */}
          <div className="form-section supplier-section">
            <h3>
              <FontAwesomeIcon icon={faShip} /> Global Supply Chain
            </h3>
            <div className="supplier-info-card">
              <FontAwesomeIcon icon={faGlobe} className="supplier-icon" />
              <div className="supplier-info">
                <strong>Globally Sourced</strong>
                <span>
                  <FontAwesomeIcon icon={faGlobe} /> Premium Global Supply Chain
                </span>
              </div>
              <span className="supplier-badge">
                <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
              </span>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faGlobe} /> Origin
                </label>
                <input
                  type="text"
                  value="Globally Sourced"
                  disabled
                  className="disabled-input"
                />
                <small className="helper-text">
                  Products are sourced from premium global suppliers
                </small>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faCity} /> Supply Chain Location
                </label>
                <input
                  type="text"
                  value="Global Distribution Center"
                  disabled
                  className="disabled-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faWarehouse} /> Warehouse
              </label>
              <input
                type="text"
                value="Global Distribution Center"
                disabled
                className="disabled-input"
              />
            </div>
          </div>

          {/* Customer & Shipping Info */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faUser} /> Customer & Shipping
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faUser} /> Customer Name
                </label>
                <input
                  type="text"
                  name="customer.name"
                  value={formData.customer?.name || ""}
                  onChange={handleChange}
                  placeholder="Customer name"
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faEnvelope} /> Customer Email
                </label>
                <input
                  type="email"
                  name="customer.email"
                  value={formData.customer?.email || ""}
                  onChange={handleChange}
                  placeholder="customer@example.com"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faPhone} /> Customer Phone
                </label>
                <input
                  type="tel"
                  name="customer.phone"
                  value={formData.customer?.phone || ""}
                  onChange={handleChange}
                  placeholder="+232 76 123456"
                />
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faWhatsapp} /> Customer WhatsApp
                </label>
                <input
                  type="tel"
                  name="customer.whatsapp"
                  value={formData.customer?.whatsapp || ""}
                  onChange={handleChange}
                  placeholder="+232 76 123456"
                />
                <small className="helper-text">For shipping updates</small>
              </div>
            </div>
            <div className="form-group">
              <label>User Type</label>
              <select
                name="customer.userType"
                value={formData.customer?.userType || "customer"}
                onChange={handleChange}
              >
                <option value="customer">Customer</option>
                <option value="farmer">Farmer</option>
              </select>
            </div>
            <div className="form-group">
              <label>Farm Name (if applicable)</label>
              <input
                type="text"
                name="customer.farmName"
                value={formData.customer?.farmName || ""}
                onChange={handleChange}
                placeholder="Farm name"
              />
            </div>

            <h4>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
            </h4>
            <div className="form-group">
              <label>Recipient Name</label>
              <input
                type="text"
                name="shippingAddress.recipientName"
                value={formData.shippingAddress?.recipientName || ""}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                name="shippingAddress.street"
                value={formData.shippingAddress?.street || ""}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="shippingAddress.city"
                  value={formData.shippingAddress?.city || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>District</label>
                <input
                  type="text"
                  name="shippingAddress.district"
                  value={formData.shippingAddress?.district || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="shippingAddress.country"
                value={formData.shippingAddress?.country || "Sierra Leone"}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faPhone} /> Shipping Phone
              </label>
              <input
                type="tel"
                name="shippingAddress.phone"
                value={formData.shippingAddress?.phone || ""}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Shipping Method */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faTruck} /> Shipping Method
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label>Shipping Method</label>
                <select
                  name="shippingMethod"
                  value={formData.shippingMethod}
                  onChange={handleChange}
                >
                  <option value="sea">
                    <FontAwesomeIcon icon={faShipIcon} /> Sea Freight (Economy)
                  </option>
                  <option value="air">
                    <FontAwesomeIcon icon={faPlane} /> Air Freight (Express)
                  </option>
                  <option value="road">
                    <FontAwesomeIcon icon={faRoad} /> Road Transport
                  </option>
                  <option value="courier">
                    <FontAwesomeIcon icon={faBox} /> Courier
                  </option>
                  <option value="combined">
                    <FontAwesomeIcon icon={faTrain} /> Combined
                  </option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faCalendar} /> Estimated Delivery
                </label>
                <input
                  type="date"
                  name="estimatedDelivery"
                  value={formData.estimatedDelivery}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Bulk Shipping Details */}
          {isBulkOrder && (
            <div className="form-section bulk-section">
              <h3>
                <FontAwesomeIcon icon={faShipIcon} /> Bulk Shipping Details
                <span className="bulk-tag">
                  <FontAwesomeIcon icon={faAnchor} /> Global Logistics
                </span>
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Container Number</label>
                  <input
                    type="text"
                    name="bulkDetails.containerNumber"
                    value={formData.bulkDetails?.containerNumber || ""}
                    onChange={handleChange}
                    placeholder="e.g., MSCU1234567"
                  />
                </div>
                <div className="form-group">
                  <label>Container Type</label>
                  <select
                    name="bulkDetails.containerType"
                    value={formData.bulkDetails?.containerType || "LCL"}
                    onChange={handleChange}
                  >
                    <option value="LCL">LCL (Less than Container)</option>
                    <option value="20ft">20ft Container</option>
                    <option value="40ft">40ft Container</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Vessel Name</label>
                  <input
                    type="text"
                    name="bulkDetails.vesselName"
                    value={formData.bulkDetails?.vesselName || ""}
                    onChange={handleChange}
                    placeholder="e.g., MAERSK CHICAGO"
                  />
                </div>
                <div className="form-group">
                  <label>Voyage Number</label>
                  <input
                    type="text"
                    name="bulkDetails.voyageNumber"
                    value={formData.bulkDetails?.voyageNumber || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faAnchor} /> Port of Loading
                  </label>
                  <input
                    type="text"
                    value="Global Supply Chain"
                    disabled
                    className="disabled-input"
                  />
                  <small className="helper-text">
                    Products are loaded from global supply chain
                  </small>
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faAnchor} /> Port of Discharge
                  </label>
                  <input
                    type="text"
                    value={SUPPLIER.portDischarge}
                    disabled
                    className="disabled-input"
                  />
                  <small className="helper-text">
                    Default: {SUPPLIER.portDischarge}
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>B/L Number</label>
                  <input
                    type="text"
                    name="bulkDetails.blNumber"
                    value={formData.bulkDetails?.blNumber || ""}
                    onChange={handleChange}
                    placeholder="Bill of Lading number"
                  />
                </div>
                <div className="form-group">
                  <label>Seal Number</label>
                  <input
                    type="text"
                    name="bulkDetails.sealNumber"
                    value={formData.bulkDetails?.sealNumber || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWeightScale} /> Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="bulkDetails.weightInKg"
                    value={formData.bulkDetails?.weightInKg || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faBoxes} /> Pallets Count
                  </label>
                  <input
                    type="number"
                    name="bulkDetails.palletsCount"
                    value={formData.bulkDetails?.palletsCount || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="bulkDetails.isBulkShipment"
                    checked={formData.bulkDetails?.isBulkShipment || false}
                    onChange={handleChange}
                  />
                  This is a Bulk Shipment
                </label>
              </div>
            </div>
          )}

          {/* Courier Details */}
          {isCourier && (
            <div className="form-section courier-section">
              <h3>
                <FontAwesomeIcon icon={faBox} /> Courier Details
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Courier Name</label>
                  <input
                    type="text"
                    name="smallShipmentDetails.courierName"
                    value={formData.smallShipmentDetails?.courierName || ""}
                    onChange={handleChange}
                    placeholder="e.g., DHL, FedEx"
                  />
                </div>
                <div className="form-group">
                  <label>Courier Tracking Number</label>
                  <input
                    type="text"
                    name="smallShipmentDetails.courierTrackingNumber"
                    value={
                      formData.smallShipmentDetails?.courierTrackingNumber || ""
                    }
                    onChange={handleChange}
                    placeholder="Tracking number"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faWeightScale} /> Package Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="smallShipmentDetails.packageWeight"
                    value={formData.smallShipmentDetails?.packageWeight || 0}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          <div className="form-section products-section">
            <h3>
              <FontAwesomeIcon icon={faBox} /> Products (
              {formData.products.length} items, {calculateTotalWeight()} kg)
              <span className="origin-summary">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
              </span>
            </h3>

            <div className="product-selector">
              <div className="form-row">
                <div className="form-group product-search-group">
                  <label>Search/Select Product</label>
                  <input
                    type="text"
                    placeholder="Search for a product..."
                    value={productSearch}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => setShowProductSelector(true)}
                  />
                  {showProductSelector && productSearch && (
                    <div className="product-search-results">
                      {filteredProducts.slice(0, 10).map((p) => (
                        <div
                          key={p._id}
                          className={`search-result ${selectedProduct?._id === p._id ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedProduct(p);
                            setProductSearch(p.name);
                          }}
                        >
                          {p.name} - ${p.basePrice?.usd || p.pricing?.usd || 0}/
                          {p.unit || "kg"}
                          <span className="product-origin">
                            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                          </span>
                        </div>
                      ))}
                      {filteredProducts.length === 0 && (
                        <div className="search-result no-results">
                          No products found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Quantity ({selectedProduct?.unit || "kg"})</label>
                  <input
                    type="number"
                    value={productQuantity}
                    onChange={(e) =>
                      setProductQuantity(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    min="1"
                    step="1"
                  />
                </div>
                <div className="form-group add-product-group">
                  <button
                    type="button"
                    className="add-product-btn"
                    onClick={handleAddProduct}
                    disabled={!selectedProduct}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </div>
            </div>

            {formData.products.length > 0 && (
              <div className="products-list">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit</th>
                      <th>Price</th>
                      <th>Origin</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.products.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td>{product.quantity}</td>
                        <td>{product.unit || "kg"}</td>
                        <td>${(product.price || 0).toFixed(2)}</td>
                        <td>
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                          </span>
                        </td>
                        <td>
                          $
                          {((product.price || 0) * product.quantity).toFixed(2)}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => handleRemoveProduct(index)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4"></td>
                      <td>
                        <strong>Total Weight:</strong>
                      </td>
                      <td>
                        <strong>{calculateTotalWeight()} kg</strong>
                      </td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan="4"></td>
                      <td>
                        <strong>From:</strong>
                      </td>
                      <td colSpan="2">
                        <strong>
                          <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Customs */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faClipboard} /> Customs Information
            </h3>
            <div className="form-row">
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="customs.cleared"
                    checked={formData.customs?.cleared || false}
                    onChange={handleChange}
                  />
                  Cleared
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="customs.dutiesPaid"
                    checked={formData.customs?.dutiesPaid || false}
                    onChange={handleChange}
                  />
                  Duties Paid
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Clearance Date</label>
                <input
                  type="date"
                  name="customs.clearanceDate"
                  value={formData.customs?.clearanceDate || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Customs Broker</label>
                <input
                  type="text"
                  name="customs.customsBroker"
                  value={formData.customs?.customsBroker || ""}
                  onChange={handleChange}
                  placeholder="Broker name"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Duty Amount (USD)</label>
              <input
                type="number"
                name="customs.dutyAmount"
                value={formData.customs?.dutyAmount || 0}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Port of Entry</label>
              <input
                type="text"
                value={SUPPLIER.portDischarge}
                disabled
                className="disabled-input"
              />
              <small className="helper-text">
                Default: {SUPPLIER.portDischarge}
              </small>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faInfoCircle} /> Notes
            </h3>
            <div className="form-group">
              <label>Public Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows="2"
                placeholder="Notes visible to customers..."
              />
            </div>
            <div className="form-group">
              <label>Internal Notes</label>
              <textarea
                name="internalNotes"
                value={formData.internalNotes || ""}
                onChange={handleChange}
                rows="2"
                placeholder="Internal notes only visible to admin..."
              />
            </div>
            <div className="form-group">
              <label>Tracking URL</label>
              <input
                type="url"
                name="trackingUrl"
                value={formData.trackingUrl || ""}
                onChange={handleChange}
                placeholder="https://tracking.example.com/..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Creating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} /> Create Shipment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShipmentForm;
